import React, { useState, useEffect } from 'react';
import OccupationService from '../services/occupationService';
import OnetAttribution from '../components/OnetAttribution';

const OccupationDataTest = () => {
  const [searchTerm, setSearchTerm] = useState('software');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const testSearch = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Test O*NET search for premium users
      const onetResult = await OccupationService.searchOccupations({
        search: searchTerm,
        userRole: 'premium',
        useOnet: true,
        limit: 10
      });
      
      setResults(onetResult);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const testComprehensiveData = async (socCode) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await OccupationService.getComprehensiveOccupationData(socCode);
      setResults(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    testSearch();
  }, []);

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-foreground">O*NET + BLS Data Integration Test</h1>
          <p className="text-muted-foreground">
            Test the integration of O*NET (1,000+ occupations) and BLS (employment & wage data) APIs
          </p>
        </div>

        {/* Search Test */}
        <div className="bg-card p-6 rounded-lg border">
          <h2 className="text-xl font-semibold mb-4">O*NET Occupation Search</h2>
          <div className="flex gap-4 mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search occupations..."
              className="flex-1 px-3 py-2 border rounded-md"
            />
            <button
              onClick={testSearch}
              disabled={loading}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md disabled:opacity-50"
            >
              {loading ? 'Searching...' : 'Search O*NET'}
            </button>
          </div>

          {results && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Found {results.data?.length || 0} occupations
              </div>
              
              {results.data?.slice(0, 5).map((occupation, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-lg">{occupation.title}</h3>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {occupation.source || 'local'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{occupation.description}</p>
                  {occupation.soc_code && (
                    <div className="text-xs text-muted-foreground">
                      SOC Code: {occupation.soc_code}
                    </div>
                  )}
                  {occupation.bls_data && (
                    <div className="text-xs text-green-600">
                      ✓ Enhanced with BLS data
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comprehensive Data Test */}
        <div className="bg-card p-6 rounded-lg border">
          <h2 className="text-xl font-semibold mb-4">Comprehensive Data Test (O*NET + BLS)</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Test getting detailed data for a specific occupation with both O*NET and BLS information
          </p>
          
          <div className="flex gap-4 mb-4">
            <input
              type="text"
              placeholder="SOC Code (e.g., 15-1132)"
              className="flex-1 px-3 py-2 border rounded-md"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  testComprehensiveData(e.target.value);
                }
              }}
            />
            <button
              onClick={() => testComprehensiveData('15-1132')}
              disabled={loading}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md disabled:opacity-50"
            >
              Test Software Developer
            </button>
          </div>

          {results && results.data && results.data.onet_data && (
            <div className="space-y-4">
              <h3 className="font-semibold">O*NET Data Available: ✓</h3>
              <h3 className="font-semibold">BLS Data Available: {results.data.bls_data ? '✓' : '✗'}</h3>
              
              <div className="text-xs text-muted-foreground">
                Last Updated: {new Date(results.data.last_updated).toLocaleString()}
              </div>
            </div>
          )}
        </div>

        {/* Data Sources Info */}
        <div className="bg-card p-6 rounded-lg border">
          <h2 className="text-xl font-semibold mb-4">Data Sources</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-green-600 mb-2">O*NET API</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• 1,000+ detailed occupations</li>
                <li>• Skills, abilities, work activities</li>
                <li>• Job requirements & qualifications</li>
                <li>• Work context & environment</li>
                <li>• Real-time search capabilities</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-blue-600 mb-2">BLS API</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Employment statistics & trends</li>
                <li>• Wage data by occupation & location</li>
                <li>• Employment projections (growth rates)</li>
                <li>• Industry data & economic indicators</li>
                <li>• Geographic variations (state-level)</li>
              </ul>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 text-destructive p-4 rounded-lg">
            Error: {error}
          </div>
        )}

        {/* O*NET Attribution */}
        <OnetAttribution />
      </div>
    </div>
  );
};

export default OccupationDataTest;
